import * as React from "react";
import { AllIconNames, Hint, Link as BaseLink } from "react-ui";

import { LINK_CLICK } from "../../metricsAction";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { emptyRef } from "../../TypingUtils";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { IconConverter } from "../../Icons/Icon";
import { tryScrollIntoViewWithFallback } from "../../../Helpers/BrowserHelpers";

import { linkTestPageObject } from "./LinkTestPageObject";

export interface LinkProps {
    bindingPath?: AbsoluteModelPath;
    children: React.ReactNode;
    disabled?: boolean;
    hint?: React.ReactNode;
    onClick: () => void;
    target?: string;
    href?: string;
    use?: "default" | "success" | "danger" | "grayed";
    metricsId?: string;
    icon?: AllIconNames | React.ReactElement;
    "data-tid"?: string;
    stopPropagation?: boolean;
}

export function Link(props: LinkProps): JSX.Element {
    const track = useMetrics();
    const linkRef = React.useRef<BaseLink>(emptyRef);

    const performClickAction = (): void => {
        track(LINK_CLICK, { href: props.href, path: props.bindingPath, metricsId: props.metricsId });
        props.onClick();
    };

    const handleClick = (e: React.MouseEvent) => {
        if (props.stopPropagation) {
            e.stopPropagation();
        }
        if (props.disabled) {
            return;
        }
        performClickAction();
    };

    useTestPageObject(() => linkTestPageObject(props, performClickAction, linkRef), [linkRef.current]);

    const rootRef = React.useRef<HTMLSpanElement>(emptyRef);

    const handleFocusRequest = (): void => {
        if (rootRef.current != undefined) {
            tryScrollIntoViewWithFallback(rootRef.current, { block: "center", inline: "center" });
        }
    };
    return (
        <FocusManagementObserver onRequestFocus={handleFocusRequest}>
            <span ref={rootRef}>
                <Hint text={props.hint} pos={"bottom center"}>
                    <BaseLink
                        icon={props.icon ? <IconConverter icon={props.icon} /> : undefined}
                        disabled={props.disabled}
                        data-tid={props["data-tid"]}
                        href={props.href}
                        target={props.target}
                        use={props.use}
                        ref={linkRef}
                        onClick={handleClick}>
                        {props.children}
                    </BaseLink>
                </Hint>
            </span>
        </FocusManagementObserver>
    );
}
